import { useState, useEffect } from 'react';
import { useTranslate, useNotify, useGetOne, useRefresh } from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormGroup,
  Checkbox,
  CircularProgress,
  Typography,
  Divider,
  Alert,
} from '@mui/material';
import { QueueSelector } from '../queue/QueueSelector';
import { channelConnectionApi } from '../../services/api.service';
import type {
  ChannelConnection,
  TicketConfigMode,
  ChannelType,
} from '../../types/chat.types';

export interface ChannelConnectionSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  channelConnectionId: number;
  channelType?: ChannelType;
}

export function ChannelConnectionSettingsDialog({
  open,
  onClose,
  channelConnectionId,
  channelType,
}: ChannelConnectionSettingsDialogProps) {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();

  const { data: connection, isLoading } = useGetOne<ChannelConnection>(
    'channel-connection',
    { id: channelConnectionId },
    { enabled: open && !!channelConnectionId }
  );

  // Form state
  const [ticketMode, setTicketMode] = useState<TicketConfigMode>('auto');
  const [defaultQueueId, setDefaultQueueId] = useState<number | null>(null);
  const [ignoreGroupMessages, setIgnoreGroupMessages] = useState(false);
  const [notifyGroupMessages, setNotifyGroupMessages] = useState(false);
  const [saving, setSaving] = useState(false);

  // Initialize form with connection data
  useEffect(() => {
    if (connection) {
      setTicketMode(connection.ticketConfig?.mode || 'auto');
      setDefaultQueueId(connection.defaultQueueId ?? null);
      setIgnoreGroupMessages(connection.config?.ignoreGroupMessages ?? false);
      setNotifyGroupMessages(connection.config?.notifyGroupMessages ?? false);
    }
  }, [connection]);

  const handleSave = async () => {
    if (!defaultQueueId) {
      notify('resources.channel.settings.queueRequired', { type: 'error' });
      return;
    }
    setSaving(true);
    try {
      await channelConnectionApi.update(channelConnectionId, {
        ticketConfig: { mode: ticketMode },
        defaultQueueId,
        config: {
          ignoreGroupMessages,
          notifyGroupMessages,
        },
      });
      notify('resources.channel.settings.saveSuccess', { type: 'success' });
      refresh();
      onClose();
    } catch (error) {
      console.error('Failed to save channel connection settings:', error);
      notify('resources.channel.settings.saveError', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const isWhatsApp = channelType === 'zapy';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{translate('resources.channel.settings.title')}</DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ pt: 1 }}>
            {/* Ticket Mode */}
            <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
              <FormLabel component="legend">
                {translate('resources.channel.settings.ticketMode')}
              </FormLabel>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {translate('resources.channel.settings.ticketModeHelp')}
              </Typography>
              <RadioGroup
                value={ticketMode}
                onChange={(e) => setTicketMode(e.target.value as TicketConfigMode)}
              >
                <FormControlLabel
                  value="auto"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1">
                        {translate('resources.channel.settings.ticketModes.auto')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {translate('resources.channel.settings.ticketModes.autoHelp')}
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="manual"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1">
                        {translate('resources.channel.settings.ticketModes.manual')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {translate('resources.channel.settings.ticketModes.manualHelp')}
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="off"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1">
                        {translate('resources.channel.settings.ticketModes.off')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {translate('resources.channel.settings.ticketModes.offHelp')}
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>

            <Divider sx={{ my: 2 }} />

            {/* Default Queue */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                {translate('resources.channel.settings.defaultQueue')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {translate('resources.channel.settings.defaultQueueHelp')}
              </Typography>
              <QueueSelector
                value={defaultQueueId}
                onChange={setDefaultQueueId}
                required
                allowClear={false}
              />
            </Box>

            {/* Group Message Handling (WhatsApp only) */}
            {isWhatsApp && (
              <>
                <Divider sx={{ my: 2 }} />
                <FormControl component="fieldset" fullWidth>
                  <FormLabel component="legend">
                    {translate('resources.channel.settings.groupMessages')}
                  </FormLabel>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {translate('resources.channel.settings.groupMessagesHelp')}
                  </Typography>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={ignoreGroupMessages}
                          onChange={(e) => {
                            setIgnoreGroupMessages(e.target.checked);
                            if (e.target.checked) {
                              setNotifyGroupMessages(false);
                            }
                          }}
                        />
                      }
                      label={translate('resources.channel.settings.ignoreGroupMessages')}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={notifyGroupMessages}
                          onChange={(e) => setNotifyGroupMessages(e.target.checked)}
                          disabled={ignoreGroupMessages}
                        />
                      }
                      label={translate('resources.channel.settings.notifyGroupMessages')}
                    />
                  </FormGroup>
                  {ignoreGroupMessages && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      {translate('resources.channel.settings.ignoreGroupMessagesWarning')}
                    </Alert>
                  )}
                </FormControl>
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {translate('ra.action.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || isLoading || !defaultQueueId}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          {translate('ra.action.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ChannelConnectionSettingsDialog;
